import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";

import apiClient from "@/lib/api-client";
import type {
  MendeleyFolderItem,
  MendeleySourceResponse,
} from "@/types/mendeley";

const QK_SOURCE = ["mendeley-source"];

export function useMendeleySource(enabled = true) {
  return useQuery({
    queryKey: QK_SOURCE,
    queryFn: () =>
      apiClient.get("api/mendeley/source").json<MendeleySourceResponse | null>(),
    enabled,
  });
}

/**
 * Starts the OAuth dance: fetches the authorize URL (server-minted HMAC
 * state) and hard-navigates to Elsevier ID. The browser returns via the
 * backend callback → /settings/integrations?connected=mendeley.
 */
export function useMendeleyConnect() {
  return useMutation({
    mutationFn: async () => {
      // credentials: "include" lets the browser store the HttpOnly state
      // cookie the API sets here; the callback requires it (audit M7).
      const { authorize_url } = await apiClient
        .get("api/mendeley/connect", { credentials: "include" })
        .json<{ authorize_url: string }>();
      window.location.assign(authorize_url);
    },
  });
}

export function useDisconnectMendeley() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => apiClient.delete("api/mendeley/source"),
    onSuccess: () => qc.invalidateQueries({ queryKey: QK_SOURCE }),
  });
}

export function useMendeleyFolders(enabled: boolean) {
  return useQuery({
    queryKey: ["mendeley-folders"],
    queryFn: () =>
      apiClient
        .get("api/mendeley/folders")
        .json<{ folders: MendeleyFolderItem[] }>(),
    enabled,
  });
}

export function useSetMendeleyFolders() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (folder_ids: string[]) =>
      apiClient
        .put("api/mendeley/folders", { json: { folder_ids } })
        .json<MendeleySourceResponse>(),
    onSuccess: () => qc.invalidateQueries({ queryKey: QK_SOURCE }),
  });
}
